import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { SignupRequest, ApiError } from '../types';
import { useI18n } from '../hooks/useI18n';
import { appConfigApi } from '../services/appConfigApi';

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  CheckCircle,
  AlertCircle,
  Leaf,
  BarChart3,
  Shield,
  Smartphone,
  Check
} from 'lucide-react';
import LanguageSwitcher from "../components/LanguageSwitcher";

const SignupPage: React.FC = () => {
  const { t, isRTL } = useI18n();
  const [formData, setFormData] = useState<SignupRequest>({
    email: '',
    login: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);

  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [registrationEnabled, setRegistrationEnabled] = useState<boolean>(true);

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      const config = await appConfigApi.getAppConfig();
      setRegistrationEnabled(config.registrationEnabled);
    };
    checkRegistrationStatus();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!registrationEnabled) return;

    setError('');

    if (formData.email && !formData.email.toLowerCase().endsWith('@gmail.com')) {
      setError(t('auth.signup.emailNotAllowed'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.signup.passwordsDoNotMatch'));
      return;
    }

    if (!agreedToTerms) {
      setError(t('auth.signup.agreeToTerms'));
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.signup(formData.email, formData.login, formData.password);

      if (response.message) {
        // Redirect to verification pending page with email
        navigate('/verification-pending', {
          state: { email: formData.email, message: response.message }
        });
      } else {
        setError('Signup failed. Please try again.');
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      setError(apiError.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { label: t('auth.signup.requirements.length'), met: formData.password.length >= 8 },
    { label: t('auth.signup.requirements.uppercase'), met: /[A-Z]/.test(formData.password) },
    { label: t('auth.signup.requirements.lowercase'), met: /[a-z]/.test(formData.password) },
    { label: t('auth.signup.requirements.number'), met: /\d/.test(formData.password) },
    { label: t('auth.signup.requirements.special'), met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) }
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <Link to="/" className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                <img src="/solvingers.svg" />
              </div>
              <h1 className="text-4xl font-bold">Facturelik</h1>
            </Link>

            <h2 className="text-3xl font-semibold mb-4">{t('auth.signup.joinTitle')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('auth.signup.joinDescription')}
            </p>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.signup.dataDrivenInsights')}</h3>
                  <p className="text-white/80">{t('auth.signup.dataDrivenDescription')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.signup.secureReliable')}</h3>
                  <p className="text-white/80">{t('auth.signup.secureDescription')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.signup.alwaysAccessible')}</h3>
                  <p className="text-white/80">{t('auth.signup.accessibleDescription')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3">
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>



          {!registrationEnabled ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Registration Disabled</h3>
              <p className="text-yellow-700">
                New user registration is currently disabled by the administrator. Please check back later.
              </p>
              <div className="mt-6">
                <Link to="/login" className="btn-primary w-full inline-block">
                  Back to Login
                </Link>
              </div>
            </div>
          )
            :
            (<>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('auth.signup.createAccount')}</h2>
                <p className="text-secondary-600">{t('auth.signup.createAccountDescription')}</p>
              </div>

              {error && (
                <div className={`mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                  <p className="text-error-800">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="form-label">
                    {t('auth.signup.emailAddress')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('auth.signup.enterEmail')}
                      className="form-input pl-10"
                      required
                      disabled={!registrationEnabled}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="login" className="form-label">
                    {t('auth.signup.username')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      type="text"
                      id="login"
                      name="login"
                      value={formData.login}
                      onChange={handleChange}
                      placeholder={t('auth.signup.enterUsername')}
                      className="form-input pl-10"
                      required
                      disabled={!registrationEnabled}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="form-label">
                    {t('auth.signup.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t('auth.signup.createPassword')}
                      className="form-input pl-10 pr-10"
                      required
                      disabled={!registrationEnabled}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                      )}
                    </button>
                  </div>

                  {/* Password Requirements */}
                  {formData.password && (
                    <div className="mt-3 p-3 bg-secondary-50 rounded-lg">
                      <p className='text-sm font-medium text-secondary-700 mb-2'>{t('auth.signup.passwordRequirements')}:</p>
                      <div className="space-y-1">
                        {passwordRequirements.map((req, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            {req.met ? (
                              <Check className="w-4 h-4 text-success-600" />
                            ) : (
                              <div className="w-4 h-4 border-2 border-secondary-300 rounded-full"></div>
                            )}
                            <span className={`text-xs ${req.met ? 'text-success-700' : 'text-secondary-600'}`}>
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    {t('auth.signup.confirmPassword')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder={t('auth.signup.confirmPasswordPlaceholder')}
                      className={`form-input pl-10 pr-10 ${formData.confirmPassword && formData.password !== formData.confirmPassword
                        ? 'border-error-500 focus:ring-error-500'
                        : ''
                        }`}
                      required
                      disabled={!registrationEnabled}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                      )}
                    </button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className='mt-1 text-sm text-error-600'>{t('auth.signup.passwordsDoNotMatch')}</p>
                  )}
                </div>

                <div className={`flex items-start ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mt-1"
                    disabled={!registrationEnabled}
                  />
                  <label htmlFor="terms" className="text-sm text-secondary-700">
                    {t('auth.signup.agreeToTermsText')}{' '}
                    <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                      {t('auth.signup.termsOfService')}
                    </Link>{' '}
                    {t('auth.signup.and')}{' '}
                    <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                      {t('auth.signup.privacyPolicy')}
                    </Link>
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !registrationEnabled}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 spinner"></div>
                      <span>{t('auth.signup.creatingAccount')}</span>
                    </div>
                  ) : (
                    t('auth.signup.createAccount')
                  )}
                </button>
              </form>
            </>
            )}
          <div className="mt-8 text-center">
            <p className="text-secondary-600">
              {t('auth.signup.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
                {t('auth.signup.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
